import { post, put } from "@common/api/apiShared";
import { useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";
import { useValidator } from "@common/hooks/validation";
import { createElement, useState } from "react";
import { useTranslation } from "../../../localization/useTranslation";
import { ISystemNotificationDialogProps } from "./SystemNotificationDialog";
import { isNullOrWhitespace } from "@common/stringOperations";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { Trans } from "react-i18next";

interface SystemNotificationDialogModel {
    title?: string;
    content?: string;
    publish: boolean;
}

export default function useSystemNotificationDialogHooks(props: ISystemNotificationDialogProps) {
    const snackbar = useDotLegalSnackbar();
    const { translateString } = useTranslation();
    const [saveModel, setSaveModel] = useState<SystemNotificationDialogModel>({
        title: props.selectedSystemNotification?.title,
        content: props.selectedSystemNotification?.content,
        publish: false,
    });

    const systemNotificationId = props.selectedSystemNotification?.id;
    const isEdit = !isNullOrWhitespace(props.selectedSystemNotification?.id);
    const isAlreadyPublished = props.selectedSystemNotification?.publishedDate !== undefined && props.selectedSystemNotification?.publishedDate !== null;

    const validation = useValidator<SystemNotificationDialogModel>(
        (validators) => ({
            title: validators.validateNotEmpty((item) => item.title, translateString("title")),
            content: validators.validateNotEmpty((item) => item.content, translateString("content")),
        }),
        []
    );

    validation.check(saveModel);

    function saveSystemNotificationAPI() {
        return post<string>("/SystemNotifications/", saveModel);
    }

    function updateSystemNotificationAPI() {
        return put<string>("/SystemNotifications/" + systemNotificationId, saveModel);
    }

    const saveMutation = useDotLegalMutation(saveSystemNotificationAPI, {
        onSuccess: (response: any) => {
            if (saveModel.publish) {
                snackbar.show(translateString("systemAnnouncementPublished"));
            } else {
                snackbar.show(createElement(Trans, { i18nKey: "createdSuccessGeneric", values: { name: saveModel?.title } }));
            }
            props.onSave();
        },
    });

    const editMutation = useDotLegalMutation(updateSystemNotificationAPI, {
        onSuccess: (response: any) => {
            if (saveModel.publish) {
                snackbar.show(translateString("systemAnnouncementPublished"));
            } else {
                snackbar.show(createElement(Trans, { i18nKey: "userEditSuccess", values: { name: saveModel?.title } }));
            }
            props.onSave();
        },
    });

    const createSystemNotification = async (publish: boolean) => {
        if (validation.anyHasErrors) {
            validation.setShowErrors(true);
            return;
        }

        if (publish && !isAlreadyPublished) {
            onPublishChange(true);
        }

        if (isEdit) {
            await editMutation.mutateAsync();
        } else {
            await saveMutation.mutateAsync();
        }
    };

    const onTitleChange = (title: string) => {
        let temp = { ...saveModel };
        temp.title = title;
        setSaveModel(temp);
    };

    const onContentChange = (content: string) => {
        let temp = { ...saveModel };
        temp.content = content;
        setSaveModel(temp);
    };

    const onPublishChange = (publish: boolean) => {
        setSaveModel({ ...saveModel, publish });
    };

    return {
        saveModel,
        onTitleChange,
        onContentChange,
        createSystemNotification,
        validation,
        isSaving: editMutation.isLoading || saveMutation.isLoading,
        isAlreadyPublished,
        isEdit,
    };
}
